import fetch from 'isomorphic-fetch';
import * as Sentry from '@sentry/browser';
import VError from 'verror';
import _ from 'lodash';
import * as oauth from 'oauth';
import { getAccessToken, removeUserAccess } from 'hooks/sessionTokens';

const fetchGoogleCloudResourceManagerApi = async (method, version, endpoint, payload = null) => {
  const baseUrl = `https://cloudresourcemanager.googleapis.com/${version}`;
  const accessToken = getAccessToken();

  const requestOptions = {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
  };

  if (method === 'POST') {
    requestOptions.body = JSON.stringify(payload);
  }

  const response = await fetch(`${baseUrl}/${endpoint}`, requestOptions);

  if (response.status === 401) {
    removeUserAccess();
    window.location.reload();
  }

  if (response.status !== 200) {
    throw new VError({
      name: 'Google Cloud Resource Manager API Error',
      info: {
        response, version, method, endpoint, payload,
      },
    }, 'Google api response');
  }

  const jsonResponse = await response.json();

  return Promise.resolve(jsonResponse);
};

const handleGoogleApiError = (message) => (reason) => {
  const formattedError = new VError({
    name: 'GoogleApiError',
    info: _.pickBy({
      code: reason?.status,
      message: reason?.body,
      reason,
    }),
  }, message);

  Sentry.captureException(formattedError);
  return Promise.reject(formattedError);
};

const filterProjectsByOrgIds = (organizationIds, projects) => _.filter(projects, (project) => {
  const orgResource = _.find(project.ancestor, (resource) => resource.resourceId.type === 'organization');
  const organizationId = _.get(orgResource, ['resourceId', 'id']);
  return _.includes(organizationIds, organizationId);
});

const makeTree = (tree, current, parents) => {
  if (!current) {
    return tree;
  }
  _.map(parents, (parent) => {
    parent.deepProjects = _.concat(parent.deepProjects || [], current?.projects || []);
    parent.deepFolders = _.concat(parent.deepFolders || [], current?.folders || []);
  });
  _.map(current?.folders, (folder) => {
    folder.folders = _.filter(tree.allFolders, { folder: { resourceId: { id: folder?.resourceId?.id } } });
    folder.projects = _.filter(tree.allProjects, { folder: { resourceId: { id: folder?.resourceId?.id } } });
    makeTree(tree, folder, [...parents, folder]);
  });
  return tree;
};

export const populateGCPFolders = (projects, organizationIds) => fetchGoogleCloudResourceManagerApi('GET', 'v3', 'folders:search')
  .then((response) => projects.map((project) => {
    if (!project.parent) {
      return project;
    }

    _.assign(project, {
      ancestor: [
        {
          resourceId: { type: 'project', id: project.projectId },
          path: `projects/${project.projectId}`,
        },
      ],
    });

    let { parent } = project;
    while (parent) {
      let nextParent;
      const ancestor = {
        resourceId: { type: parent.type, id: parent.id },
        path: `${parent.type}s/${parent.id}`,
        displayName: undefined,
      };

      if (parent.type === 'folder') {
        const folder = _.find(_.get(response, 'folders'), { name: `folders/${parent.id}` });
        if (folder) {
          _.assign(ancestor, folder);
          const [type, id] = folder.parent.split('/');
          nextParent = {
            id,
            type: _.trimEnd(type, 's'), // resource type is derived from pluralized parent path
          };
        }
      }

      project.ancestor.push(ancestor);
      parent = nextParent;
    }
    return project;
  }))
  .then((mappedProjects) => {
    const tree = {};
    const projectsToImport = _.isEmpty(organizationIds) ? mappedProjects : filterProjectsByOrgIds(organizationIds, mappedProjects);
    tree.allProjects = projectsToImport;
    tree.allFolders = [];
    _.map(projectsToImport, (project) => {
      const parentFolders = _.filter(project?.ancestor, (ancestor) => !(/^(projects|organizations)/i).test(`${ancestor?.path}`));
      project.id = project?.projectId;
      let previous = null;
      _.map(parentFolders, (parentFolder) => {
        const existing = _.find(tree.allFolders, { resourceId: { id: parentFolder?.resourceId?.id } });
        if (previous) {
          previous.folder = parentFolder;
        }
        if (!existing) {
          parentFolder.id = parentFolder?.resourceId?.id;
          parentFolder.name = parentFolder?.displayName || parentFolder?.resourceId?.id;
          tree.allFolders.push(parentFolder);
        } else {
          existing.id = parentFolder?.resourceId?.id;
          existing.name = parentFolder?.displayName || parentFolder?.resourceId?.id;
        }
        previous = parentFolder;
      });
      project.folder = _.head(parentFolders);
    });
    tree.folders = _.reject(tree.allFolders, 'folder');
    tree.projects = _.reject(tree.allProjects, 'folder');
    makeTree(tree, tree, [tree]);
    tree.deepFolders = tree.allFolders;
    tree.deepProjects = tree.allProjects;
    return tree;
  })
  .catch(handleGoogleApiError('Failed to get Google folders'));

const fetchAllProjectsWithPageToken = ({ pageToken = null, pageSize = 2000, filter = null }) => {
  let endpoint = `projects?pageSize=${pageSize}`;
  if (pageToken) {
    endpoint += `&pageToken=${pageToken}`;
  }
  if (filter) {
    endpoint += `&filter=${filter}`;
  }
  return fetchGoogleCloudResourceManagerApi('GET', 'v1', endpoint)
    .then(async (response) => {
      if (response?.nextPageToken) {
        const nextProjects = await fetchAllProjectsWithPageToken({ pageToken: response.nextPageToken, filter });
        return _.concat(response.projects, nextProjects);
      }
      return response.projects;
    });
};

export const getGCPProjects = ({
  includeFolders = false,
  organizationIds,
  filter,
} = {}) => fetchAllProjectsWithPageToken({ pageToken: null, filter })
  .then((projects) => _.filter(projects, (project) => project?.parent // Projects must be associated w/ an Organization
    && project?.lifecycleState !== 'DELETE_REQUESTED'))
  .then((projects) => (includeFolders ? populateGCPFolders(projects, organizationIds) : projects))
  .catch(handleGoogleApiError('Failed to get Google projects'));

export const getUnmanagedProjects = ({
  organizationIds,
  managedProjectIds,
  filter,
} = {}) => fetchAllProjectsWithPageToken({ pageToken: null, filter })
  .then((projects) => _.filter(projects, (project) => project?.parent // Projects must be associated w/ an Organization
        && project?.lifecycleState !== 'DELETE_REQUESTED' && !_.includes(managedProjectIds, project.projectId)))
  .then((projects) => populateGCPFolders(projects, organizationIds))
  .catch(handleGoogleApiError('Failed to get Google projects'));

export const getGCPOrganizations = async () => {
  if (!oauth.isSignedIn()) {
    return [];
  }

  return fetchGoogleCloudResourceManagerApi('GET', 'v3', 'organizations:search');
};

export const getIamPolicy = (organizationDomain) => fetchGoogleCloudResourceManagerApi('POST', 'v3', `${organizationDomain}:getIamPolicy`, {})
  .then((response) => response);

export const roleMap = {
  'Organization Viewer': 'roles/resourcemanager.organizationViewer',
  'Project Browser': 'roles/browser',
  'Project Creator': 'roles/resourcemanager.projectCreator',
};

export const extractRoles = (rolesMap) => (
  _.compact(_.map(rolesMap, (value, key) => _.get(roleMap, key)))
);

/**
 * Add a member to the bindings for a set of IAM roles
 *
 * @param  {String} type - IAM member identity type (user|group|domain|serviceAccount)
 * @param  {String} principal - IAM member identifier
 * @param  {String[]} roles - IAM role
 * @param  {Object} policy [doc](https://cloud.google.com/iam/reference/rest/v1/Policy)
 * @return {Object} policy bindings [doc](https://cloud.google.com/iam/reference/rest/v1/Policy#Binding)
 */

const addBindingsForMember = (type, principal, roles, policyBindings) => {
  const newBindings = _.map(roles, (role) => {
    if (_.some(policyBindings, (binding) => binding.role === role)) {
      return null;
    }
    return { role, members: [`${type}:${principal}`] };
  });
  const modifiedBindings = _.map(policyBindings, (binding) => {
    if (_.includes(roles, binding.role)) {
      const members = _.uniq(_.concat(binding.members, `${type}:${principal}`));
      return _.assign({}, binding, { members });
    }
    return binding;
  });
  const bindings = _.compact(_.concat(newBindings, modifiedBindings));
  return { bindings };
};

export const setIamOrgPolicy = (organizationDomain, currentpolicy, roles) => {
  const proposedPolicy = { policy: { ...currentpolicy, ...addBindingsForMember('serviceAccount', process.env.REACT_APP_AUTOMATION_SERVICE_ACCOUNT, roles, currentpolicy.bindings) } };
  return fetchGoogleCloudResourceManagerApi('POST', 'v3', `${organizationDomain}:setIamPolicy`, proposedPolicy)
    .then()
    .catch((reason) => Promise.reject(new VError({
      name: 'GoogleApiError',
      info: {
        code: reason?.status,
        message: reason?.body,
        reason,
      },
    }, 'Failed to update google setIamOrgPolicy')));
};

export const testIamPermissions = (organizationDomain, requiredPermissions) => fetchGoogleCloudResourceManagerApi('POST', 'v3', `${organizationDomain}:testIamPermissions`, { permissions: requiredPermissions })
  .then()
  .catch(handleGoogleApiError('Failed to test for IAM permissions'));

export const listOrgPolicies = (organizationDomain) => fetchGoogleCloudResourceManagerApi('POST', 'v1', `${organizationDomain}:listOrgPolicies`, {}).then()
  .catch(handleGoogleApiError('Failed to test for Org policy constraints'));
